import { GameAdHandlerConfig } from '@/modules/main-frame/ads/game-ad-handler.types'
import { GameEvent } from '@/types'

import { CloudSaveMessageHandler } from '../cloud-saves'
import { InGamePaymentsMessageHandler } from '../in-game-payments'
import { PlatformService } from '../platform-service'
import { SafeStorageMessageHandler } from '../safe-storage'
import { UserService } from '../user-service'
import type { GameAdHandler } from './ads/game-ad-handler'
import { MessageAction, OutcomingMessage } from './message-types'

const VERSION = 1
const SOURCE = 'GameEvents'

function generateMessageId() {
    return `${Date.now()}-${Math.random()}`
}

export type MainFrameOptions = {
    currentLanguage: string
    gameAdHandler: GameAdHandler
    storageMessageHandler: SafeStorageMessageHandler
    inGamePaymentsMessageHandler: InGamePaymentsMessageHandler
    cloudSaveMessageHandler: CloudSaveMessageHandler
    userService: UserService
    onLogin: () => void
    onGameStart: () => void
}

function sendMessage({ action, originalMessageEvent, payload, type }: OutcomingMessage) {
    console.info(action, type, payload)
    originalMessageEvent.source.postMessage(
        {
            action,
            id: generateMessageId(),
            payload: payload || {},
            responseToId: originalMessageEvent.data.id,
            source: SOURCE,
            type: type || originalMessageEvent.data.type,
            ver: VERSION,
        },
        // @ts-ignore: Incorrect overload for postMessage in lib.dom.d.ts
        '*',
    )
}

function gameAdHandlerCallback(event: CustomEvent<GameAdHandlerConfig['adMessage']>) {
    if (event.detail) {
        sendMessage(event.detail as OutcomingMessage)
    }
}

const constructErrorMessage = (event: GameEvent, error: string) =>
    ({
        action: MessageAction.unknownMessageType,
        originalMessageEvent: event as GameEvent,
        payload: {
            error,
        },
        type: 'error',
    }) as OutcomingMessage

export class MainFrameService {
    private readonly bindedMessageHandler

    private gameAdHandler: GameAdHandler

    private userService: UserService

    private platformService: PlatformService

    private storageMessageHandler: SafeStorageMessageHandler

    private inGamePaymentsMessageHandler: InGamePaymentsMessageHandler

    private cloudSaveMessageHandler: CloudSaveMessageHandler

    private onLogin: () => void

    private onGameStart: () => void
    private onGameStartCalled = false

    private bindedStickyRecalculateBanner?: (event: ScreenOrientationEventMap['change']) => void

    constructor(options: MainFrameOptions) {
        this.bindedMessageHandler = this.messageHandler.bind(this)
        window.addEventListener('message', this.bindedMessageHandler)

        const {
            userService,
            storageMessageHandler,
            gameAdHandler,
            inGamePaymentsMessageHandler,
            cloudSaveMessageHandler,
            onLogin,
            onGameStart,
        } = options
        this.userService = userService
        this.platformService = new PlatformService(options.currentLanguage)
        this.storageMessageHandler = storageMessageHandler
        this.inGamePaymentsMessageHandler = inGamePaymentsMessageHandler
        this.cloudSaveMessageHandler = cloudSaveMessageHandler
        this.onLogin = onLogin
        this.onGameStart = onGameStart

        this.gameAdHandler = gameAdHandler
        this.gameAdHandler.eventBus.addEventListener('adMessage', gameAdHandlerCallback)
    }

    destroy() {
        window.removeEventListener('message', this.bindedMessageHandler)
        this.gameAdHandler.eventBus.removeEventListener('adMessage', gameAdHandlerCallback)
        if (this.bindedStickyRecalculateBanner) {
            window.screen.orientation.removeEventListener('change', this.bindedStickyRecalculateBanner)
        }
    }

    messageHandler(event: MessageEvent) {
        if (event.data.source !== SOURCE || !event.source) {
            return
        }

        const { action, type } = event.data

        console.info(action, type)

        switch (type) {
            case 'liveness':
                if (!this.onGameStartCalled) {
                    this.onGameStart()
                    this.onGameStartCalled = true
                }
                sendMessage({
                    action: MessageAction.pong,
                    originalMessageEvent: event as GameEvent,
                })

                break

            case 'adv':
                this.gameAdHandler.handleMessage(event as GameEvent)
                break

            case 'storage':
                this.storageMessageHandler(event as GameEvent).then((msg) => {
                    if (msg) {
                        sendMessage(msg)
                    }
                })

                break
            case 'user':
                this.userService?.handleMessage(event as GameEvent, this.onLogin).then((msg) => {
                    if (msg) {
                        sendMessage(msg)
                    }
                })

                break
            case 'in-game-payments':
                this.inGamePaymentsMessageHandler(event as GameEvent).then((msg) => {
                    sendMessage(msg)
                })

                break
            case 'cloud-save':
                this.cloudSaveMessageHandler(event as GameEvent).then((msg) => {
                    if (msg) {
                        sendMessage(msg)
                    }
                })

                break

            case 'platform':
                sendMessage(this.platformService.handleMessage(event as GameEvent))

                break

            default:
                sendMessage(constructErrorMessage(event as GameEvent, `Unknown message type "${type}"`))
        }
    }

    addEventListener: GameAdHandler['eventBus']['addEventListener'] = (...args) => {
        this.gameAdHandler.eventBus.addEventListener(...args)
    }

    removeEventListener: GameAdHandler['eventBus']['removeEventListener'] = (...args) => {
        this.gameAdHandler.eventBus.removeEventListener(...args)
    }
}
